'use client';

import { PropsWithChildren, ReactNode } from 'react';
import clsx, { ClassValue } from 'clsx';

export const searchFieldDropdownPanelClass =
  'max-h-[min(20rem,70dvh)] rounded-md border border-gray-3 bg-white py-0 shadow-md';

type SearchFieldDropdownMenuProps = PropsWithChildren<{
  className?: ClassValue;
}>;

export const SearchFieldDropdownMenu = ({
  className,
  children,
}: SearchFieldDropdownMenuProps) => (
  <ul role="listbox" className={clsx('w-full py-1', className)}>
    {children}
  </ul>
);

type SearchFieldDropdownItemProps = {
  selected?: boolean;
  onSelect: () => void;
  children: ReactNode;
  className?: ClassValue;
};

export const SearchFieldDropdownItem = ({
  selected = false,
  onSelect,
  children,
  className,
}: SearchFieldDropdownItemProps) => (
  <li role="presentation" className="w-full">
    <button
      type="button"
      role="option"
      aria-selected={selected}
      onClick={onSelect}
      className={clsx(
        '!flex !w-full !min-h-11 items-center text-black-1 !justify-start !bg-transparent px-3 !py-3 !text-left text-sm normal-case transition-colors',
        'touch-manipulation lg:!min-h-0 lg:!py-1.5',
        'focus-visible:outline-none',
        selected
          ? '!font-bold !text-primary-main'
          : 'font-normal hover:!text-secondary-main',
        className
      )}
    >
      <span className="block min-w-0 flex-1 truncate text-left text-inherit">
        {children}
      </span>
    </button>
  </li>
);

type SearchFieldDropdownSearchProps = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  inputRef?: React.RefObject<HTMLInputElement | null>;
  'aria-label'?: string;
};

export const SearchFieldDropdownSearch = ({
  value,
  onChange,
  placeholder = 'Search',
  inputRef,
  'aria-label': ariaLabel = 'Search',
}: SearchFieldDropdownSearchProps) => (
  <div className="border-b border-gray-2 px-2 py-1.5">
    <input
      ref={inputRef}
      type="search"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      aria-label={ariaLabel}
      className="w-full rounded border-0 bg-gray-1 px-2 py-1.5 text-left text-sm font-normal normal-case text-black-1 placeholder:text-gray-6 focus:bg-white focus:outline-none focus:ring-1 focus:ring-primary-main"
    />
  </div>
);

export const SearchFieldDropdownEmpty = ({
  children,
}: PropsWithChildren) => (
  <p className="px-3 py-2 text-center text-sm font-normal normal-case text-gray-7">
    {children}
  </p>
);
